<script setup lang="ts">
/**
 * Singbac — Declaration of Directors' Interest (Section 165 + Section 156).
 * per client-director (DocScope dir_{id}/). 관계회사(holding/ultimate/subsidiary/fellow) 셀은 각 변수.
 * 원문의 [[if x]][[x]][[else]]-[[endif]] 의 '-' 기본값은 백엔드 처리이므로 화면에선 무시.
 */
import { inject, ref, watch, onMounted } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

// Section 156 "회사/펌 × Nature of Interest" — 행 추가/삭제 반복부([[if s156]]…[[/if s156]]).
//  per client-director(dir_{id}/) 스코프. 화면 키 s156_{n}_firm / s156_{n}_nature → normalize 가 s156[] 배열로.
const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
let seq156 = 1;
const s156Rows = ref<number[]>([1]);
function addS156() {
  s156Rows.value.push(++seq156);
}
function delS156(id: number) {
  if (s156Rows.value.length > 1) s156Rows.value = s156Rows.value.filter((x) => x !== id);
}
/** 값이 채워진 s156 행 id 목록(편집 재진입). */
function detectS156(): number[] {
  const present: number[] = [];
  for (let i = 1; i <= 30; i++) {
    const has = ['firm', 'nature'].some((s) => String(fill.values[`${scope}s156_${i}_${s}`] ?? '').trim());
    if (has) present.push(i);
  }
  return present;
}
function syncS156() {
  const present = detectS156();
  if (!present.length) return;
  const merged = Array.from(new Set([...s156Rows.value, ...present])).sort((a, b) => a - b);
  if (merged.length !== s156Rows.value.length || merged.some((v, i) => v !== s156Rows.value[i])) {
    s156Rows.value = merged;
  }
  seq156 = Math.max(seq156, ...merged);
}
onMounted(syncS156);
watch(() => detectS156().join(','), syncS156);
</script>

<template>
  <div class="allot-doc">
    <div class="doc-sec-title">DECLARATION OF DIRECTORS' INTEREST IN THE COMPANY AND ITS RELATED CORPORATIONS</div>

    <p class="doc-p">
      Name : <CddVarField field-key="client_name" label="Client Name" /><br />
      Date : <CddVarField field-key="disclosure_date" label="Disclosure Date" />
    </p>

    <p class="doc-p">The Directors,</p>
    <p class="doc-p">
      <CddVarField field-key="company_name" label="Company Name" />.<br />
      <CddVarField field-key="registered_office_address" label="Registered Office Address" />
    </p>

    <p class="doc-p">Dear Sir/Madam</p>

    <p class="doc-p">
      <CddVarField field-key="company_name" label="Company Name" />. – "the Company"<br />
      Financial period <CddVarField field-key="financial_period" label="Financial Period" />
    </p>

    <p class="doc-p">
      In accordance with Section 165 of the Companies Act, I hereby give notice that I am interested in the following
      shares and/or debentures of the Company, its holding company, its subsidiaries and/or fellow subsidiaries, as
      registered in the names indicated below:
    </p>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <th>Name of Registered Holder</th>
          <th>Shares/debentures/options/warrants</th>
          <th>Par Value SGD(S$)</th>
          <th>Balance at the date of appointment (Total Shares)</th>
          <th>Balance at the end of Financial Year</th>
          <th>Date acquired &amp; price</th>
        </tr>
        <tr>
          <td>Interest in Company in Own Name (<CddVarField field-key="company_name" label="Company Name" />)</td>
          <td><CddVarField field-key="own_name_type" label="Own Name — Type" /></td>
          <td><CddVarField field-key="par_value" label="Par Value" /></td>
          <td><CddVarField field-key="own_name_balance" label="Own Name — Balance at Appointment" /></td>
          <td><CddVarField field-key="own_name_fy_end" label="Own Name — Balance at FY End" /></td>
          <td><CddVarField field-key="own_name_acquisition" label="Own Name — Date Acquired and Price" /></td>
        </tr>
        <tr>
          <td>Interest in Company's Holding Company</td>
          <td><CddVarField field-key="holding_type" label="Holding — Type" /></td>
          <td><CddVarField field-key="holding_par_value" label="Holding — Par Value" /></td>
          <td><CddVarField field-key="holding_balance" label="Holding — Balance at Appointment" /></td>
          <td><CddVarField field-key="holding_fy_end" label="Holding — Balance at FY End" /></td>
          <td><CddVarField field-key="holding_acquisition" label="Holding — Date Acquired and Price" /></td>
        </tr>
        <tr>
          <td>Interest In Company's Ultimate holding company</td>
          <td><CddVarField field-key="ultimate_type" label="Ultimate — Type" /></td>
          <td><CddVarField field-key="ultimate_par_value" label="Ultimate — Par Value" /></td>
          <td><CddVarField field-key="ultimate_balance" label="Ultimate — Balance at Appointment" /></td>
          <td><CddVarField field-key="ultimate_fy_end" label="Ultimate — Balance at FY End" /></td>
          <td><CddVarField field-key="ultimate_acquisition" label="Ultimate — Date Acquired and Price" /></td>
        </tr>
        <tr>
          <td>Interest in Company's Subsidiaries</td>
          <td><CddVarField field-key="subsidiary_type" label="Subsidiary — Type" /></td>
          <td><CddVarField field-key="subsidiary_par_value" label="Subsidiary — Par Value" /></td>
          <td><CddVarField field-key="subsidiary_balance" label="Subsidiary — Balance at Appointment" /></td>
          <td><CddVarField field-key="subsidiary_fy_end" label="Subsidiary — Balance at FY End" /></td>
          <td><CddVarField field-key="subsidiary_acquisition" label="Subsidiary — Date Acquired and Price" /></td>
        </tr>
        <tr>
          <td>Interest in Fellow Subsidiaries</td>
          <td><CddVarField field-key="fellow_type" label="Fellow — Type" /></td>
          <td><CddVarField field-key="fellow_par_value" label="Fellow — Par Value" /></td>
          <td><CddVarField field-key="fellow_balance" label="Fellow — Balance at Appointment" /></td>
          <td><CddVarField field-key="fellow_fy_end" label="Fellow — Balance at FY End" /></td>
          <td><CddVarField field-key="fellow_acquisition" label="Fellow — Date Acquired and Price" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="text-align:center"><i>*Signature page to follow*</i></p>
    <p class="doc-p">Yours Faithfully,</p>
    <div class="sig-line">________________________</div>
    <p class="doc-p"><CddVarField field-key="client_name" label="Client Name" /></p>

    <p class="doc-lbl-title">NOTES:</p>
    <p class="doc-p">
      (i) A director's interest in shares or debentures are deemed to include shares or debentures held by the director's
      spouse or infant children, including step and adopted children.
    </p>
    <p class="doc-p">
      Shares or debentures registered in the names of nominees in which a director has a beneficial interest should also
      be disclosed.
    </p>
    <p class="doc-p">
      Shares or debentures registered in the names of directors, the beneficial interest of which is vested in another
      party, should be disclosed together with details of the beneficial shareholder(s).
    </p>
    <p class="doc-p">
      (ii) This refers to companies or corporations in which a director has a controlling interest or together with his
      associates control 20% or more of the voting shares therein or if the companies or corporations or its directors are
      accustomed or under an obligation to act in accordance with the directors' directions, instructions or wishes.
    </p>
    <p class="doc-p">(iii) If space is insufficient, please attach list.</p>

    <div class="page-div">Section 156</div>

    <p class="doc-p">
      Name : <CddVarField field-key="client_name" label="Client Name" /><br />
      Date : <CddVarField field-key="disclosure_date" label="Disclosure Date" />
    </p>

    <p class="doc-p">The Directors,</p>
    <p class="doc-p">
      <CddVarField field-key="company_name" label="Company Name" /><br />
      <CddVarField field-key="registered_office_address" label="Registered Office Address" />
    </p>

    <p class="doc-p">Dear Sir/Madam,</p>

    <div class="doc-sec-title">DIRECTOR'S DISCLOSURE OF INTERESTS IN TRANSACTIONS, PROPERTY, OFFICES ETC.</div>

    <p class="doc-p">
      In accordance with Section 156 of the Companies Act 1967, I hereby give the company general notice that I am an
      officer or member of the corporations or firms set out below and should be regarded as being interested in any
      transaction hereinafter made with any of the undermentioned corporations or firms.
    </p>

    <!-- repeat: s156 ([[if s156]] … [[/if s156]]) — 회사/펌 별 행 추가/삭제 -->
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <th>Name of Company or Firm</th>
          <th>Nature of Interest (Director or Member)</th>
        </tr>
        <tr v-for="(id, i) in s156Rows" :key="id">
          <td>
            <CddVarField :field-key="`s156_${id}_firm`" :label="`Company/Firm ${i + 1}`" />
            <button v-if="s156Rows.length > 1" class="row-del" title="Remove" @click="delS156(id)">✕</button>
          </td>
          <td><CddVarField :field-key="`s156_${id}_nature`" :label="`Nature of Interest ${i + 1}`" /></td>
        </tr>
        <tr><td colspan="2"><button class="row-add" @click="addS156">+ Add company/firm</button></td></tr>
      </tbody>
    </table>

    <p class="doc-p">
      I shall be obliged if you would ensure that the above interests are brought up and read at the next meeting of the
      directors and that this declaration of interests is duly recorded in the minutes of that meeting.
    </p>

    <p class="doc-p">Yours faithfully</p>
    <p class="doc-p">…………………………………………. <CddVarField field-key="client_name" label="Client Name" /></p>
  </div>
</template>

<style scoped>
.doc-lbl-title { font-weight: 800; margin: 14px 0 6px; }
.sig-line { font-family: monospace; margin-top: 18px; }
.row-del { margin-left: 6px; border: none; background: none; color: #c0392b; cursor: pointer; font-size: 11px; }
.row-add { border: 1px dashed #9aa4b2; background: #f7f8fa; border-radius: 6px; padding: 2px 8px; font-size: 11px; color: #3182f6; cursor: pointer; }
</style>
